import { useSession, useUser } from "@descope/react-sdk";
import {
	Box,
	CircularProgress, LinearProgress,
	Stack, Typography
} from "@mui/material";
import axios from "axios";
import jwtDecode from "jwt-decode";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import apiRoutes from "../../constants/api-routes";
import pages from "../../constants/pages";
import {
	setDescopeUserId,
	setEmail, setFirstName, setGroups,
	setIsCoAppSuperAdmin,
	setLastName, setOrganizationForceLogin, setOrganizationIdentityProvider,
	setOrganizationKillSwitch, setOrganizationName,
	setOrganizationSsoAndScim, setOrgId, 	setPermissions, setPhone, setRoles,
	setUserId} from "../../redux/userSlice";
import {
	CoAppLoginCard,
	CoAppLoginContainer,
	CoAppLoginFooter
} from "../global/styled/global.styled";
import CoAppDescopeLoginFlow from "./CoAppDescopeLoginFlow";


export default function PortalSignIn() {
	const [countdown, setCountdown] = useState(30);
	const { isAuthenticated, isSessionLoading } = useSession();
	const { isUserLoading } = useUser();
	const [loginCompleted, setLoginCompleted] = useState(false);
	const [progress, setProgress] = useState(0);
	const [userDoesNotExist, setUserDoesNotExist] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const handleLogin = (e: any) => {
		const decodedJwt = jwtDecode(e.detail.sessionJwt) as { email?: string; tenantId?: string };
		const email = decodedJwt.email ?? "";
		const tenantId = decodedJwt.tenantId ?? "";
		dispatch(setEmail(email ?? ""));
		axios.get(apiRoutes.getUser(1, "", email, tenantId), { headers: { "Authorization": "Bearer " + e.detail.sessionJwt } })
			.then(res => {
				dispatch(setFirstName(res.data.firstname));
				dispatch(setLastName(res.data.lastname));
				dispatch(setGroups(res.data.groups));
				dispatch(setIsCoAppSuperAdmin(res.data.isCoAppSupport));
				dispatch(setUserId(res.data.id));
				dispatch(setPhone(res.data.phone));
				dispatch(setOrganizationForceLogin(res.data.organization.forceLoginActive));
				dispatch(setOrganizationKillSwitch(res.data.organization.killSwitchActive));
				dispatch(setOrganizationSsoAndScim(res.data.organization.isSsoAndScimEnabled));
				if (res.data.organization.isSsoAndScimEnabled) {
					dispatch(setOrganizationIdentityProvider(res.data.organization.identityProviderName));
				}
				dispatch(setOrgId(res.data.organizationId));
				dispatch(setOrganizationName(res.data.organization?.name));
				dispatch(setPermissions(res.data.permissions));
				dispatch(setRoles(res.data.roles));
				dispatch(setDescopeUserId(res.data.descopeUserId));
				setLoginCompleted(true);
			})
			.catch(err => {
				console.log("Error getting user: " + err);
				axios.post(apiRoutes.syncByTenantId, { descopeTenantId: e.detail.user.userTenants[0].tenantId },
					{ headers: { "Authorization": "Bearer " + e.detail.sessionJwt } })
					.then(() => {
						setUserDoesNotExist(true);
					});
			});
	};

	useEffect(() => {
		let interval;
		if (userDoesNotExist) {
			interval = setInterval(() => {
				setCountdown(prevCountdown => {
					const newCountdown = prevCountdown - 1;
					setProgress(((30 - newCountdown) / 30) * 100);
					if (newCountdown <= 0) {
						clearInterval(interval);
						window.location.reload();
					}
					return newCountdown;
				});
			}, 1000);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [userDoesNotExist, navigate]);

	useEffect(() => {
		if ((isAuthenticated && !isSessionLoading && !userDoesNotExist) || loginCompleted) {
			navigate(pages.home);
		}
	}, [isAuthenticated, isSessionLoading, userDoesNotExist, loginCompleted, navigate]);

	// Shared chrome — every state renders inside the same brand container so
	// transitions between loading → flow → user-doesn't-exist don't flash a
	// different layout.
	return (
		<CoAppLoginContainer>
			<CoAppLoginCard>
				{/* Descope's flow renders its own CoApp wordmark inside the form,
				    so we no longer render a second one here. (The duplicate
				    logo was the visible bug — keep this comment so nobody
				    re-adds <CoAppLoginLogo> without matching the Descope
				    flow config.) */}

				{(isSessionLoading || isUserLoading) && (
					<Stack spacing={2} sx={{ py: 4, alignItems: "center" }}>
						<CircularProgress sx={{ color: "#02D5AB" }} size={40} thickness={4} />
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)" }}>
							Signing you in…
						</Typography>
					</Stack>
				)}

				{userDoesNotExist && (
					<Stack spacing={2} sx={{ py: 2, alignItems: "center" }}>
						<Typography variant="body1" sx={{ color: "rgba(13, 31, 48, 0.85)", textAlign: "center", fontWeight: 500 }}>
							Setting up your account
						</Typography>
						<Typography variant="body2" sx={{ color: "rgba(13, 31, 48, 0.55)", textAlign: "center" }}>
							Please wait {Math.max(countdown, 0)} seconds and try logging in again.
						</Typography>
						<Box sx={{ width: "100%", mt: 1 }}>
							<LinearProgress
								variant="determinate"
								value={progress}
								sx={{
									height: 6,
									borderRadius: 3,
									backgroundColor: "rgba(2, 213, 171, 0.12)",
									"& .MuiLinearProgress-bar": {
										background: "linear-gradient(90deg, #02D5AB 0%, #0A7FA8 100%)",
										borderRadius: 3
									}
								}}
							/>
						</Box>
					</Stack>
				)}

				{!isSessionLoading && !isUserLoading && !userDoesNotExist && (
					<CoAppDescopeLoginFlow onSuccess={handleLogin} />
				)}

				<CoAppLoginFooter>
					<a href="https://coapp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
					<span aria-hidden="true">·</span>
					<a href="https://coapp.io/terms"   target="_blank" rel="noopener noreferrer">Terms of Service</a>
					<span aria-hidden="true">·</span>
					<a href="mailto:support@coapp.io">Support</a>
				</CoAppLoginFooter>
			</CoAppLoginCard>
		</CoAppLoginContainer>
	);
}